import { useNavigate, useLocation } from "react-router-dom";
import { 
  Home, 
  Package, 
  Grid, 
  Users, 
  BadgeDollarSign, 
  Settings, 
  Calendar, 
  Clock, 
  List,
  CheckCircle,
  AlertCircle,
  XCircle,
  ChevronDown,
  ChevronUp,
  Share2,
  CreditCard,
  PlusCircle,
  ShieldCheck,
  BarChart,
  Link2
} from "lucide-react";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useTranslation } from "react-i18next";

export interface SidebarMenuItem {
  icon: React.ReactNode;
  label: string;
  hasSubmenu?: boolean;
  isOpen?: boolean;
  submenuItems?: Array<{
    label: string;
    icon?: React.ReactNode;
    onClick?: () => void;
    isSelected?: boolean;
    description?: string;
  }>;
  onClick?: () => void;
  isSelected?: boolean;
  linkTo?: string;
  description?: string;
  className?: string;
}

interface DashboardMenuProps {
  activeView: string;
  activeSubmenu: string | null;
  toggleSubmenu: (menuName: string) => void;
  handleViewChange: (view: string) => void;
  handleSettingsSectionChange?: (section: string) => void;
  hasRequests?: boolean;
}

export const DashboardMenu = ({ 
  activeView, 
  activeSubmenu, 
  toggleSubmenu, 
  handleViewChange,
  handleSettingsSectionChange,
  hasRequests = false
}: DashboardMenuProps) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const [isAdmin, setIsAdmin] = useState(false);
  
  // Check if user has admin role
  useEffect(() => {
    const checkAdminStatus = async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        
        if (user) {
          // Fetch the user's profile to check their role
          const { data: profileData, error: profileError } = await supabase
            .from('profiles')
            .select('role')
            .eq('id', user.id)
            .single();
            
          if (!profileError && profileData && profileData.role === 'admin') {
            setIsAdmin(true);
          }
        }
      } catch (error) {
        console.error('Error checking admin status:', error);
      }
    };
    
    checkAdminStatus();
  }, []);

  const serviceRequestsMenuItems = [
    { 
      label: t('navigation.calendar'), 
      icon: <Calendar className="h-4 w-4" />, 
      onClick: () => {
        handleViewChange("service-requests");
        navigate("/dashboard/service-requests?view=calendar");
      },
      isSelected: location.pathname === "/dashboard/service-requests" && location.search.includes("view=calendar"),
    },
    { 
      label: t('navigation.upcoming'), 
      icon: <Clock className="h-4 w-4" />, 
      onClick: () => {
        handleViewChange("service-requests");
        navigate("/dashboard/service-requests?view=upcoming");
      },
      isSelected: location.pathname === "/dashboard/service-requests" && location.search.includes("view=upcoming"),
    },
    { 
      label: t('navigation.allRequests'), 
      icon: <List className="h-4 w-4" />, 
      onClick: () => {
        handleViewChange("service-requests");
        navigate("/dashboard/service-requests?view=all");
      },
      isSelected: location.pathname === "/dashboard/service-requests" && location.search.includes("view=all"),
    },
    { 
      label: t('navigation.pending'), 
      icon: <AlertCircle className="h-4 w-4" />, 
      onClick: () => {
        handleViewChange("service-requests");
        navigate("/dashboard/service-requests?view=pending");
      },
      isSelected: location.pathname === "/dashboard/service-requests" && location.search.includes("view=pending"),
    },
    { 
      label: t('navigation.scheduled'), 
      icon: <Clock className="h-4 w-4" />, 
      onClick: () => {
        handleViewChange("service-requests");
        navigate("/dashboard/service-requests?view=scheduled");
      },
      isSelected: location.pathname === "/dashboard/service-requests" && location.search.includes("view=scheduled"),
    },
    { 
      label: t('navigation.completed'), 
      icon: <CheckCircle className="h-4 w-4" />, 
      onClick: () => {
        handleViewChange("service-requests");
        navigate("/dashboard/service-requests?view=completed");
      },
      isSelected: location.pathname === "/dashboard/service-requests" && location.search.includes("view=completed"),
    },
    { 
      label: t('navigation.rejected'), 
      icon: <XCircle className="h-4 w-4" />, 
      onClick: () => {
        handleViewChange("service-requests");
        navigate("/dashboard/service-requests?view=rejected");
      },
      isSelected: location.pathname === "/dashboard/service-requests" && location.search.includes("view=rejected"),
    },
    { 
      label: t('navigation.create'), 
      icon: <PlusCircle className="h-4 w-4" />, 
      onClick: () => {
        handleViewChange("service-requests");
        navigate("/dashboard/service-requests?view=create");
      },
      isSelected: location.pathname === "/dashboard/service-requests" && location.search.includes("view=create"),
    }
  ];

  const serviceMenuItems = [
    { 
      label: t('navigation.all'), 
      icon: <List className="h-4 w-4" />, 
      onClick: () => {
        handleViewChange("services");
        navigate("/dashboard/services");
      },
      isSelected: location.pathname === "/dashboard/services",
    },
    { 
      label: t('navigation.category'), 
      icon: <Package className="h-4 w-4" />, 
      onClick: () => {
        handleViewChange("categories");
        navigate("/dashboard/categories");
      },
      isSelected: location.pathname === "/dashboard/categories",
    },
  ];

  const settingsMenuItems = [
    { 
      label: t('navigation.general'), 
      icon: <Settings className="h-4 w-4" />,
      description: "Manage your profile and account preferences",
      onClick: () => {
        handleViewChange("settings");
        handleSettingsSectionChange("general");
      },
      isSelected: activeView === "settings" && location.pathname === "/dashboard/settings/general",
    },
    { 
      label: t('navigation.business'), 
      icon: <BadgeDollarSign className="h-4 w-4" />,
      description: "Configure your business details and payment options",
      onClick: () => {
        handleViewChange("settings");
        handleSettingsSectionChange("business");
      },
      isSelected: activeView === "settings" && location.pathname === "/dashboard/settings/business",
    },
    { 
      label: t('navigation.notifications'), 
      icon: <AlertCircle className="h-4 w-4" />,
      description: "Configure your notification preferences",
      onClick: () => {
        handleViewChange("settings");
        handleSettingsSectionChange("notifications");
      },
      isSelected: activeView === "settings" && location.pathname === "/dashboard/settings/notifications",
    },
    { 
      label: 'Social Links',
      icon: <Link2 className="h-4 w-4" />, 
      description: "Manage your social and external links",
      onClick: () => {
        handleViewChange("settings");
        handleSettingsSectionChange("social-links");
      },
      isSelected: activeView === "settings" && location.pathname === "/dashboard/settings/social-links",
    },
    { 
      label: t('navigation.payments'), 
      icon: <CreditCard className="h-4 w-4" />, 
      onClick: () => {
        handleViewChange("settings");
        navigate("/dashboard/settings/payments");
      },
      isSelected: activeView === "settings" && location.pathname === "/dashboard/settings/payments",
    },
    { 
      label: t('navigation.marketing'), 
      icon: <BarChart className="h-4 w-4" />,
      description: "Manage your marketing and promotional tools",
      onClick: () => {
        handleViewChange("settings");
        navigate("/dashboard/settings/marketing");
      },
      isSelected: activeView === "settings" && location.pathname === "/dashboard/settings/marketing",
    },
    { 
      label: t('navigation.availability'), 
      icon: <Calendar className="h-4 w-4" />,
      description: "Manage your availability schedule",
      onClick: () => {
        handleViewChange("settings");
        navigate("/dashboard/settings/availability");
      },
      isSelected: activeView === "settings" && location.pathname === "/dashboard/settings/availability",
    },
    // Add admin settings option for admin users
    ...(isAdmin ? [{ 
      label: "Admin", 
      icon: <ShieldCheck className="h-4 w-4" />, 
      description: "Admin-only settings and tools", 
      onClick: () => {
        handleViewChange("settings");
        navigate("/dashboard/settings/admin");
        if (handleSettingsSectionChange) {
          handleSettingsSectionChange("admin");
        }
      },
      isSelected: location.pathname === "/dashboard/settings/admin",
    }] : [])
  ];

  const sidebarItems: SidebarMenuItem[] = [
    { 
      icon: <Home />, 
      label: t('navigation.home'), 
      linkTo: "/dashboard",
      isSelected: activeView === "overview",
      onClick: () => {
        handleViewChange("overview");
      },
      className: "tour-overview"
    },
    { 
      icon: <Grid />, 
      label: t('navigation.serviceRequests'),
      hasSubmenu: true,
      isOpen: activeSubmenu === "service-requests",
      submenuItems: serviceRequestsMenuItems,
      onClick: () => toggleSubmenu("service-requests"),
      isSelected: activeView === "service-requests",
      className: "tour-service-requests"
    },
    { 
      icon: <Package />, 
      label: t('navigation.services'),
      hasSubmenu: true,
      isOpen: activeSubmenu === "services",
      submenuItems: serviceMenuItems,
      onClick: () => toggleSubmenu("services"),
      isSelected: activeView === "services" || activeView === "categories",
      className: "tour-services"
    },
    { 
      icon: <Users />, 
      label: t('navigation.customers'),
      isSelected: activeView === "customers",
      onClick: () => {
        handleViewChange("customers");
      },
      className: "tour-customers"
    },
    { 
      icon: <Settings />, 
      label: t('navigation.settings'),
      hasSubmenu: true,
      isOpen: activeSubmenu === "settings",
      submenuItems: settingsMenuItems,
      onClick: () => toggleSubmenu("settings"),
      isSelected: activeView === "settings",
      className: "tour-settings"
    }
  ];

  return { sidebarItems };
};

export default DashboardMenu;
